import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="collapsible-section"
export default class extends Controller {
  static targets = ["body", "expandIcon", "collapseIcon"];
  static values = { expanded: { type: Boolean, default: false } };

  connect() {
    // Add CSS transition to body for smooth collapse/expand
    if (this.hasBodyTarget) {
      this.bodyTarget.style.transition =
        "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      // Use overflow: hidden to prevent content from showing during animation
      this.bodyTarget.style.overflow = "hidden";
    }
    this.setState(this.expandedValue);
  }

  toggle() {
    this.setState(!this.expandedValue);
  }

  setState(open) {
    this.expandedValue = open;

    if (this.hasBodyTarget) {
      if (open) {
        // Expand: remove hidden class first, then set max-height
        this.bodyTarget.classList.remove("hidden");
        this.bodyTarget.style.opacity = "0";

        // Use requestAnimationFrame to ensure DOM is updated before reading scrollHeight
        requestAnimationFrame(() => {
          const height = this.bodyTarget.scrollHeight;
          this.bodyTarget.style.maxHeight = height + "px";
          this.bodyTarget.style.opacity = "1";

          // After animation completes, set max-height to none for dynamic content
          setTimeout(() => {
            if (this.expandedValue) {
              this.bodyTarget.style.maxHeight = "none";
              this.bodyTarget.style.overflow = "visible";
            }
          }, 400);
        });
      } else {
        // Set explicit height before collapsing for smooth animation
        const height = this.bodyTarget.scrollHeight;
        this.bodyTarget.style.maxHeight = height + "px";
        this.bodyTarget.style.overflow = "hidden";

        // Force reflow
        this.bodyTarget.offsetHeight;

        // Collapse: set max-height to 0 for smooth animation
        requestAnimationFrame(() => {
          this.bodyTarget.style.maxHeight = "0px";
          this.bodyTarget.style.opacity = "0";
        });

        // Hide after transition completes
        setTimeout(() => {
          if (!this.expandedValue) {
            this.bodyTarget.classList.add("hidden");
          }
        }, 400);
      }
    }

    // Handle icon toggling - supports two patterns:
    // 1. Dual icons (expandIcon + collapseIcon) - toggle visibility
    // 2. Single icon (expandIcon only) - rotate the icon
    if (this.hasExpandIconTarget && this.hasCollapseIconTarget) {
      // Pattern 1: Dual icons (e.g., expand-square.svg and collapse-square.svg)
      if (open) {
        // When expanded, hide expand icon and show collapse icon
        this.expandIconTarget.classList.add("hidden");
        this.collapseIconTarget.classList.remove("hidden");
      } else {
        // When collapsed, show expand icon and hide collapse icon
        this.expandIconTarget.classList.remove("hidden");
        this.collapseIconTarget.classList.add("hidden");
      }
    } else if (this.hasExpandIconTarget) {
      // Pattern 2: Single icon with rotation (e.g., chevron-down.svg)
      if (open) {
        this.expandIconTarget.classList.add("rotate-180");
      } else {
        this.expandIconTarget.classList.remove("rotate-180");
      }
    }

    this.element.setAttribute("aria-expanded", open ? "true" : "false");
  }
}
